import React from 'react';
import {View} from 'react-native';

import {F} from '../courseTheme';
import {EngineScore, evalWord, formatEval, whiteShare} from './engineLogic';

type Props = {
  /** White-POV score of the best line; null before the first line. */
  score: EngineScore | null | undefined;
  /** Match the board's width so the bar runs its full height. */
  height: number;
  /** True when the board shows Black at the bottom. */
  flipped?: boolean;
  width?: number;
};

/**
 * A slim vertical eval bar for beside a course board.
 *
 * Local rather than Study's EvalBar: that one prints its score at 9px inside
 * the bar, and these screens keep nothing under 12px. The number lives in the
 * engine panel; the bar carries the same reading for a screen reader in words.
 */
export default function CourseEvalBar({score, height, flipped = false, width = 14}: Props) {
  const share = whiteShare(score);
  const label = score ? `Evaluation ${formatEval(score)}, ${evalWord(score)}` : 'Evaluation not available yet';
  return (
    <View
      accessible
      accessibilityRole="image"
      accessibilityLabel={label}
      style={{
        width,
        height,
        backgroundColor: F.brownDark,
        borderRadius: 4,
        borderWidth: 1,
        // The fill is white on a white card; a 4.6:1 edge keeps the bar's
        // extent visible however far White is ahead.
        borderColor: F.mutedText,
        overflow: 'hidden',
        position: 'relative',
      }}>
      <View
        testID="eval-white-fill"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: `${share}%`,
          backgroundColor: F.white,
          ...(flipped ? {top: 0} : {bottom: 0}),
        }}
      />
      {/* The equality line, so "slightly better" reads against something. */}
      <View style={{position: 'absolute', left: 0, right: 0, top: '50%', height: 1, backgroundColor: 'rgba(225,120,70,0.9)'}} />
    </View>
  );
}
